import {
    useImperativeHandle,
    forwardRef,
    useEffect,
    Fragment,
    useRef,
    useId
} from "react";
import {
    AccessibilityInfo,
    Platform,
    View
} from "react-native";
import type IActionSheetProps from "./type";
import type {
    ActionSheetHeaderWebPropsType,
    ActionSheetHeadingType,
    ActionSheetItemType,
    IActionSheetRef
} from "./type";
import stylesheet, {
    ACTION_SHEET_DESCRIPTION_VARIANT,
    getActionSheetIsDividerVisible,
    getActionSheetActionRadius,
    ACTION_SHEET_TITLE_VARIANT,
    useStyles
} from "./stylesheet";
import {
    NCoreUIKitLocalize,
    NCoreUIKitTheme
} from "../../../../core/hooks";
import type {
    RefForwardingComponent,
    NCoreUIKitIcon
} from "../../../../types";
import {
    usePointerType
} from "../../../../helpers/pointer";
import {
    useOptionRowHighlights
} from "../../../optionRow/util";
import {
    ChevronLeft as ChevronLeftIcon
} from "lucide-react-native";
import {
    getOptionRowInset
} from "../../../optionRow/stylesheet";
import {
    warnOnce
} from "../../../../utils/development";
import PopoverSurfaceHeading from "../../../popoverSurface/components/heading";
import AnimatedIcon from "../../../animatedIcon";
import OptionRow from "../../../optionRow";
import Button from "../../../button";
import Text from "../../../text";
import BottomSheet from "../..";

const ActionSheetBackIcon: NCoreUIKitIcon = ({
    customColor,
    color,
    size
}) => <AnimatedIcon
    customColor={customColor}
    animationTrigger="hover"
    icon={ChevronLeftIcon}
    animation="signature"
    color={color}
    size={size}
/>;

const getActionSheetElement = (node: unknown) => {
    if(typeof HTMLElement === "undefined" || !(node instanceof HTMLElement)) {
        return null;
    }

    return node;
};

const ActionSheet: RefForwardingComponent<IActionSheetRef, IActionSheetProps> = ({
    isShowCancel = true,
    bottomSheetProps,
    customLocalize,
    primaryAction,
    cancelTitle,
    customTheme,
    description,
    backTitle,
    children,
    isActive,
    onClosed,
    onOpened,
    onClose,
    action,
    onBack,
    onOpen,
    items,
    title
}, ref) => {
    const {
        controlPaddings,
        controlHeights,
        touchTargets,
        typography,
        iconSizes,
        radiuses,
        borders,
        colors,
        spaces
    } = NCoreUIKitTheme.useContext(customTheme);

    const {
        localize
    } = NCoreUIKitLocalize.useContext(customLocalize);

    const pointerType = usePointerType();

    const baseId = useId();

    const {
        getHighlightProps,
        isHighlighted
    } = useOptionRowHighlights();

    const sheetRef = useRef<IActionSheetRef>(null);

    const headerRef = useRef<View>(null);

    const titleId = `${baseId}-title`;

    const descriptionId = `${baseId}-description`;

    const isHeaderVisible = !!title || !!description || !!onBack || !!action;

    const isFooterVisible = isShowCancel || !!primaryAction;

    const {
        hiddenDivider: hiddenDividerDynamicStyle,
        headerText: headerTextDynamicStyle,
        divider: dividerDynamicStyle,
        action: actionDynamicStyle,
        footer: footerDynamicStyle,
        header: headerDynamicStyle,
        back: backDynamicStyle,
        list: listDynamicStyle
    } = useStyles({
        controlPaddings,
        controlHeights,
        touchTargets,
        pointerType,
        typography,
        iconSizes,
        borders,
        colors,
        spaces
    });

    const actionRadius = getActionSheetActionRadius({
        controlPaddings,
        controlHeights,
        typography,
        radiuses,
        borders,
        spaces
    });

    useImperativeHandle(ref, () => ({
        setIsContentReady: (status) => {
            sheetRef.current?.setIsContentReady(status);
        },
        close: (onClosedCallback) => {
            sheetRef.current?.close(onClosedCallback);
        },
        open: () => {
            sheetRef.current?.open();
        }
    }));

    useEffect(() => {
        if(title) {
            return;
        }

        warnOnce({
            message: "ActionSheet: a \"title\" (the trigger's name when the menu has none) is needed so screen readers can name the sheet."
        });
    }, [
        title
    ]);

    const focusHeader = () => {
        const header = headerRef.current;

        if(!header || bottomSheetProps?.modalProps?.initialFocusRef) {
            return;
        }

        if(Platform.OS === "web") {
            getActionSheetElement(header)?.focus({
                preventScroll: true
            });

            return;
        }

        AccessibilityInfo.sendAccessibilityEvent(header, "focus");
    };

    const handleOpened = () => {
        focusHeader();

        onOpen?.();

        onOpened?.();
    };

    const handleOpenChange = (isNextOpen: boolean) => {
        if(isNextOpen) {
            return;
        }

        onClose?.({
            id: baseId
        });
    };

    const handleCancel = () => {
        onClose?.({
            id: baseId
        });

        sheetRef.current?.close();
    };

    const handleItemPress = ({
        isCloseOnPress,
        onPress
    }: Pick<ActionSheetItemType, "isCloseOnPress" | "onPress">) => {
        onPress?.();

        if(isCloseOnPress !== false) {
            onClose?.({
                id: baseId
            });

            sheetRef.current?.close();
        }
    };

    const renderBack = () => {
        if(!onBack) {
            return null;
        }

        return <Button
            accessibilityLabel={backTitle ? localize("go-back-to", [
                backTitle
            ]) : localize("go-back")}
            icon={ActionSheetBackIcon}
            customTheme={customTheme}
            style={backDynamicStyle}
            spreadBehaviour="free"
            onPress={onBack}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderAction = () => {
        if(!action) {
            return null;
        }

        return <Button
            accessibilityLabel={action.accessibilityLabel}
            isDisabled={action.isDisabled}
            isLoading={action.isLoading}
            borderRadius={actionRadius}
            style={actionDynamicStyle}
            customTheme={customTheme}
            onPress={action.onPress}
            spreadBehaviour="free"
            title={action.title}
            variant="ghost"
            type="neutral"
            size="small"
        />;
    };

    const renderTitle = () => {
        if(!title) {
            return null;
        }

        return <Text
            variant={ACTION_SHEET_TITLE_VARIANT}
            customTheme={customTheme}
            role="heading"
            id={titleId}
            color="high"
        >
            {title}
        </Text>;
    };

    const renderDescription = () => {
        if(!description) {
            return null;
        }

        return <Text
            variant={ACTION_SHEET_DESCRIPTION_VARIANT}
            customTheme={customTheme}
            id={descriptionId}
            color="mid"
        >
            {description}
        </Text>;
    };

    const renderHeader = () => {
        return <View
            style={[
                stylesheet.header,
                headerDynamicStyle
            ]}
        >
            {renderBack()}
            <View
                style={[
                    stylesheet.headerText,
                    headerTextDynamicStyle
                ]}
                ref={headerRef}
                tabIndex={-1}
            >
                {renderTitle()}
                {renderDescription()}
            </View>
            {renderAction()}
        </View>;
    };

    const renderFooter = () => {
        return <View
            style={[
                stylesheet.footer,
                footerDynamicStyle
            ]}
        >
            {
                isShowCancel ?
                    <Button
                        title={cancelTitle ?? localize("cancel")}
                        style={stylesheet.footerButton}
                        isPressScaleEnabled={false}
                        customTheme={customTheme}
                        onPress={handleCancel}
                        spreadBehaviour="free"
                        variant="outline"
                        type="neutral"
                        size="large"
                    />
                :
                    null
            }
            {
                primaryAction ?
                    <Button
                        accessibilityLabel={primaryAction.accessibilityLabel}
                        isDisabled={primaryAction.isDisabled}
                        isLoading={primaryAction.isLoading}
                        style={stylesheet.footerButton}
                        onPress={primaryAction.onPress}
                        isPressScaleEnabled={false}
                        title={primaryAction.title}
                        customTheme={customTheme}
                        spreadBehaviour="free"
                        size="large"
                    />
                :
                    null
            }
        </View>;
    };

    const renderDivider = (item: ActionSheetItemType, previousEntry?: ActionSheetHeadingType | ActionSheetItemType) => {
        if(!previousEntry || "heading" in previousEntry) {
            return null;
        }

        const isVisible = getActionSheetIsDividerVisible({
            isPreviousHighlighted: isHighlighted(previousEntry.key),
            isNextHighlighted: isHighlighted(item.key)
        });

        return <View
            style={[
                stylesheet.divider,
                isVisible ? dividerDynamicStyle : hiddenDividerDynamicStyle
            ]}
            role="separator"
        />;
    };

    const renderEntry = (entry: ActionSheetHeadingType | ActionSheetItemType, index: number) => {
        if("heading" in entry) {
            return <PopoverSurfaceHeading
                style={entry.isReserveLeading ? {
                    marginStart: getOptionRowInset({
                        variant: "sheet",
                        controlHeights,
                        touchTargets,
                        pointerType,
                        typography,
                        iconSizes,
                        spaces
                    })
                } : undefined}
                customTheme={customTheme}
                variant="sheet"
                key={entry.key}
            >
                {entry.heading}
            </PopoverSurfaceHeading>;
        }

        const {
            isCloseOnPress,
            tabIndex = 0,
            onPress,
            key,
            ...itemProps
        } = entry;

        return <Fragment
            key={key}
        >
            {renderDivider(entry, items?.[index - 1])}
            <OptionRow
                {...itemProps}
                {...getHighlightProps(key)}
                onPress={() => handleItemPress({
                    isCloseOnPress,
                    onPress
                })}
                role={entry.role ?? "menuitem"}
                customTheme={customTheme}
                tabIndex={tabIndex}
                variant="sheet"
            />
        </Fragment>;
    };

    const renderList = () => {
        if(!items?.length) {
            return null;
        }

        const listWebProps: ActionSheetHeaderWebPropsType = {
            "aria-labelledby": title ? titleId : undefined
        };

        return <View
            {...listWebProps}
            style={[
                stylesheet.list,
                listDynamicStyle
            ]}
            role="menu"
        >
            {items.map(renderEntry)}
        </View>;
    };

    return <BottomSheet
        {...bottomSheetProps}
        modalProps={{
            ...bottomSheetProps?.modalProps,
            "aria-describedby": description ? descriptionId : bottomSheetProps?.modalProps?.["aria-describedby"],
            "aria-labelledby": title ? titleId : bottomSheetProps?.modalProps?.["aria-labelledby"]
        }}
        renderHeader={isHeaderVisible ? renderHeader : undefined}
        renderBottom={isFooterVisible ? renderFooter : undefined}
        customLocalize={customLocalize}
        onOpenChange={handleOpenChange}
        customTheme={customTheme}
        onOpened={handleOpened}
        isCustomPadding={true}
        onClosed={onClosed}
        isAutoHeight={true}
        isShowClose={false}
        isOpen={isActive}
        ref={sheetRef}
    >
        {children}
        {renderList()}
    </BottomSheet>;
};
export default forwardRef(ActionSheet);
